import { useCallback, useRef, useState } from 'react';
import { SubmitHandler, useFieldArray, UseFormReturn } from 'react-hook-form';

import { useDebounceCallback } from 'usehooks-ts';
import { v4 as uuid } from 'uuid';

import {
  MAX_NO_OF_POLL_QUESTION_OPTIONS,
  MIN_NO_OF_POLL_QUESTION_OPTIONS,
  POLL_SAVE_DEBOUNCE_TIME,
} from '../../../constants';
import { FormValues } from '../../../PollQuestionForm/types';

export interface UseUnpublishedQuestionOptions {
  formMethods: UseFormReturn<FormValues>;
  onSubmit: SubmitHandler<FormValues>;
  onLocalSubmit: SubmitHandler<FormValues>;
}

const useUnpublishedQuestionOptions = ({
  formMethods,
  onSubmit,
  onLocalSubmit,
}: UseUnpublishedQuestionOptions) => {
  const bottomElementRef = useRef<HTMLDivElement | null>(null);

  const [canAutoFocusOption, setCanAutoFocusOption] = useState(false);

  const { handleSubmit, control } = formMethods;

  const fieldArrayMethods = useFieldArray({
    control,
    name: 'options',
  });

  const { fields, remove, append } = fieldArrayMethods;

  const canRemoveOption = fieldArrayMethods.fields.length > MIN_NO_OF_POLL_QUESTION_OPTIONS;

  const handleAppendOption = async () => {
    if (fields.length >= MAX_NO_OF_POLL_QUESTION_OPTIONS) return;
    append({ optionId: uuid(), text: '', type: 'text' });

    // wait for rendering new option
    await new Promise((resolve) => setTimeout(resolve, 100));

    setCanAutoFocusOption(true);

    // let API know that we have a new option
    handleSubmit(onSubmit)();

    if (!bottomElementRef.current) return;
    // scroll to bottom to show new option
    bottomElementRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleRemoveOption = (optionIndex: number) => async () => {
    remove(optionIndex);
    // let API know that we have deleted an option
    await handleSubmit(onSubmit)();
    await handleSubmit(onLocalSubmit)();
  };

  const onChange = useCallback(
    async (name: string, value: string) => {
      const getUpdatedData = (data: FormValues) => {
        return {
          ...data,
          options: data.options.map((option, index) => {
            if (name === `options.${index}.text`) return { ...option, text: value };
            return option;
          }),
        };
      };

      const submitForm = handleSubmit((data) => onSubmit(getUpdatedData(data)));
      await submitForm();

      const localSubmitForm = handleSubmit((data) => onLocalSubmit(getUpdatedData(data)));
      await localSubmitForm();
    },
    [handleSubmit, onSubmit, onLocalSubmit]
  );

  const handleChangeQuestionOption = useDebounceCallback(onChange, POLL_SAVE_DEBOUNCE_TIME);

  const handleBlurQuestionOption = useCallback(() => {
    handleSubmit(onLocalSubmit)();
  }, [handleSubmit, onLocalSubmit]);

  return {
    bottomElementRef,
    fields,
    canRemoveOption,
    canAutoFocusOption,
    handleAppendOption,
    handleRemoveOption,
    handleChangeQuestionOption,
    handleBlurQuestionOption,
  };
};

export default useUnpublishedQuestionOptions;
